import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Tooltip from 'LuxKit/Tooltip'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCreateExperienceSelectionSession from 'hooks/ExperiencePage/useCreateExperienceSelectionSession'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import {
  ARENA_SCRIPT_SRC,
  arenaEvents,
  defaultArenaOptions,
  areAllArenaSeatsMatchedToTickets,
  getArenaCustomerSeatHoverMessages,
  getArenaCustomLegend,
  getArenaDisplaySeatPrice,
  getArenaDisplayTotalPrice,
  getArenaEventStateUpdate,
  getArenaReservationFailureReason,
  getArenaReservationHold,
  getTrolleyToken,
  hasMixedArenaReservableSeatGroups,
  loadArenaScript,
  patchArenaZoomControls,
} from './arenaSeatSelectionUtils'
import { createArenaTrolleyToken, createArenaTrolleyReservation, releaseArenaTransaction } from './arenaSeatSelectionApi'
import type {
  ArenaApp,
  ArenaConfigOptions,
  ArenaCreateOptions,
  ArenaCurrency,
  ArenaErrorType,
  ArenaEventName,
  ArenaEventPayload,
  ArenaReservationFailureReason,
  ArenaReservationHold,
  ArenaSelectedSeat,
  ArenaSelectionResult,
  ArenaSendMethod,
  ArenaSeatAvailabilityLegend,
  ArenaStatus,
} from './arenaSeatSelectionUtils'

interface ArenaErrorState {
  type: ArenaErrorType
  eventName?: ArenaEventName
  reservationFailureReason?: ArenaReservationFailureReason
}

interface Props {
  apiBase: string
  displayCurrencyCode: string
  eventId: string
  experienceId: string
  onSelectionComplete: (result: ArenaSelectionResult) => void
  perfId: string
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot
  tyrBase: string
  options?: ArenaConfigOptions
  scriptSrc?: string
}

declare global {
  interface Window {
    IngressoArena?: {
      create: (options: ArenaCreateOptions) => Promise<ArenaApp>
    }
  }
}

const Container = styled.section`
  width: 100%;
`

const AvailabilityHeading = styled(Heading)`
  margin-bottom: ${rem(16)};
`

const Shell = styled.div`
  width: 100vw;
  margin-inline: calc(50% - 50vw);
  overflow: hidden;
  background-color: var(--palette-neutral-surface-light-background);

  ${mediaQueryUp.tablet} {
    width: 100%;
    margin-inline: 0;
    border-radius: ${props => props.theme.borderRadius.M};
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(16)} ${rem(16)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)};
  }
`

const ErrorPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  align-self: flex-start;
  padding: ${rem(6)} ${rem(10)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.messaging.warning.lightBackground};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: 0 ${rem(16)} ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(16)} ${rem(16)};
  }
`

const ArenaPanel = styled.div`
  width: 100%;
  min-width: 0;
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  overflow: hidden;

  igo-arena {
    display: block;
    width: 100%;
    min-height: clamp(${rem(352)}, 46dvh, ${rem(448)});
    --igo-map-height: clamp(${rem(320)}, 42dvh, ${rem(400)});
    --igo-map-width: 100%;
  }
`

const ArenaLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)} ${rem(12)};
  align-items: center;
  justify-content: center;
  min-height: ${rem(40)};
  padding: ${rem(10)} ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const ArenaLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};

  > *:not(:first-child) {
    display: flex;
    align-items: center;
    min-height: ${rem(20)};
  }
`

const SelectedSeatsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`

const BookingFooter = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: ${rem(12)};
  row-gap: ${rem(4)};
  padding-top: ${rem(20)};
  border-top: 1px solid var(--palette-neutral-surface-light-foreground);

  ${mediaQueryUp.tablet} {
    display: flex;
    gap: ${rem(20)};
  }
`

const BookingFooterCopy = styled.div`
  grid-column: 1;
  grid-row: 1;
  flex-shrink: 0;
  min-width: 0;
`

const EarnPoints = styled.div`
  display: flex;
  grid-column: 1;
  grid-row: 2;
  flex: 1;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
`

const BookNowButton = styled(TextButton)`
  grid-column: 3;
  grid-row: 1 / span 2;
  white-space: nowrap;
  --button-color: var(--palette-highlight-primary-dark-contrast);
  --button-background-color: var(--palette-highlight-primary-dark-background);
  --button-border-color: var(--palette-highlight-primary-dark-background);
  --button-hover-color: var(--palette-highlight-primary-dark-contrast);
  --button-hover-background-color: color-mix(in srgb, var(--palette-highlight-primary-dark-background), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
  --button-hover-border-color: var(--button-hover-background-color);
  --button-active-color: var(--palette-highlight-primary-dark-contrast);
  --button-active-background-color: color-mix(in srgb, var(--palette-highlight-primary-dark-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
  --button-active-border-color: var(--button-active-background-color);
`

const CartButton = styled(IconButton)`
  grid-column: 2;
  grid-row: 1 / span 2;
  --button-internal-default-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-default-background-color: var(--palette-neutral-surface-static-white);
  --button-internal-default-border-color: var(--palette-neutral-surface-light-foreground);
  --button-internal-hover-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-hover-background-color: var(--palette-neutral-surface-light-background);
  --button-internal-hover-border-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-active-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-active-background-color: var(--palette-neutral-surface-light-background);
  --button-internal-active-border-color: var(--palette-highlight-primary-dark-foreground);
`

const SeatList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin: ${rem(24)} 0 0;
  padding: 0;
  list-style: none;
`

const SeatItem = styled.li`
  display: grid;
  grid-template-columns: ${rem(12)} minmax(0, 1fr) auto ${rem(40)};
  align-items: center;
  gap: ${rem(20)};
  padding: ${rem(16)} ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const SeatDetails = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(2)};
`

const SeatPoints = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  min-height: ${rem(20)};
`

const SeatPrice = styled(BodyText)`
  white-space: nowrap;
`

const SeatRestrictionWarning = styled(BodyText)`
  grid-column: 2 / -1;
`

const SeatRemoveButton = styled(IconButton)`
  --icon-button-size: ${rem(32)};
  --icon-button-icon-size: ${rem(20)};
  --button-internal-default-color: var(--palette-neutral-surface-light-foreground-alt);
  --button-internal-default-background-color: var(--palette-neutral-surface-static-white);
  --button-internal-default-border-color: var(--palette-neutral-surface-light-foreground);
`

const ColourSwatch = styled.span<{ $bgColor?: string, $borderColor?: string }>`
  display: block;
  flex: none;
  width: ${rem(12)};
  height: ${rem(12)};
  border-radius: 50%;
  background-color: ${props => props.$bgColor ?? props.theme.palette.neutral.default.five};
  border: 1px solid ${props => props.$borderColor ?? props.theme.palette.neutral.default.four};
`

function getErrorLabel(intl: ReturnType<typeof useIntl>, error: ArenaErrorState) {
  switch (error.type) {
    case 'arena-api-unavailable':
      return intl.formatMessage({
        id: '_AJJfktgKzPjEEujOeKx',
        defaultMessage: 'Arena script loaded, but the Arena API was not available.',
        description: 'Error shown when the Ingresso Arena script loads without exposing the expected browser API',
      })
    case 'arena-create-failed':
      return intl.formatMessage({
        id: 'r_9PKLa6KxAUB3-k5JEo',
        defaultMessage: 'Arena could not start the seat map.',
        description: 'Error shown when the Ingresso Arena seat map cannot be created',
      })
    case 'event-failed':
      return intl.formatMessage({
        id: '-ODJG8XIuM-zmzMZmqsx',
        defaultMessage: 'Arena event failed: {eventName}',
        description: 'Error shown when Ingresso Arena emits a failure event',
      }, { eventName: error.eventName })
    case 'missing-trolley-token':
      return intl.formatMessage({
        id: 'BMpk4KKAZzlvYWJ_BAFQ',
        defaultMessage: 'We couldn’t prepare those seats for checkout. Please choose different seats and try again.',
        description: 'Customer-facing error shown when selected Ingresso Arena seats cannot be prepared for checkout',
      })
    case 'reserve-failed':
      switch (error.reservationFailureReason) {
        case 'leaves-single-seat':
          return intl.formatMessage({
            id: 'kaxmhfd07QcCg-be_1o7',
            defaultMessage: 'Those seats would leave a single seat unavailable. Please choose seats together and try again.',
            description: 'Customer-facing error shown when selected Ingresso Arena seats would leave a single seat unavailable',
          })
        case 'missing-transaction':
          return intl.formatMessage({
            id: '-3bkt_02hAECWFcBXoY3',
            defaultMessage: 'We couldn’t confirm your seat reservation. Please choose different seats and try again.',
            description: 'Customer-facing error shown when Ingresso Arena cannot confirm the seat reservation',
          })
        case 'seat-unavailable':
          return intl.formatMessage({
            id: 'dAQN20pKPTt19zmW9skw',
            defaultMessage: 'We couldn’t reserve one or more of those seats. Please choose different seats and try again.',
            description: 'Customer-facing error shown when one or more selected Ingresso Arena seats cannot be reserved',
          })
        case 'some-seats-unavailable':
          return intl.formatMessage({
            id: 'B6U28xEYnIVq1UPqhjNm',
            defaultMessage: 'Some of those seats could not be reserved. Please choose different seats and try again.',
            description: 'Customer-facing error shown when some selected Ingresso Arena seats cannot be reserved',
          })
        case 'reserve-failed':
        default:
          return intl.formatMessage({
            id: '4uFo60MiA7zqGhn-NLW4',
            defaultMessage: 'We couldn’t reserve those seats. Please choose different seats and try again.',
            description: 'Customer-facing error shown when selected Ingresso Arena seats cannot be reserved',
          })
      }
    case 'script-load-failed':
      return intl.formatMessage({
        id: 'XJMt-S1SewSdOx-tzLUP',
        defaultMessage: 'Arena could not load the seat map.',
        description: 'Error shown when the Ingresso Arena browser script cannot be loaded',
      })
    case 'selection-session-failed':
    default:
      return intl.formatMessage({
        id: 'hpHcAI-IXln_eZFC_kvw',
        defaultMessage: 'Arena could not create a selection session for this offer.',
        description: 'Error shown when the app cannot create an Ingresso Arena selection session',
      })
  }
}

function waitForTrolleyToken(resolverRef: React.MutableRefObject<((token: string) => void) | undefined>) {
  return new Promise<string | undefined>((resolve) => {
    const timeout = window.setTimeout(() => {
      if (resolverRef.current === resolveToken) resolverRef.current = undefined
      resolve(undefined)
    }, 5000)

    function resolveToken(token: string) {
      window.clearTimeout(timeout)
      resolve(token)
    }

    resolverRef.current = resolveToken
  })
}

function isReservationError(error: ArenaErrorState) {
  return error.type === 'missing-trolley-token' || error.type === 'reserve-failed'
}

function removeArenaSeat(root: ShadowRoot, seatUuid: string | undefined) {
  if (!seatUuid) return

  const mapSeat = root.querySelector<SVGElement>(`#arn-${seatUuid}`)
  mapSeat?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true }))
}

function patchArenaSeatHoverMessages(root: ParentNode, restrictedViewLabel: string, seatMessages: Map<string, string>) {
  function clearWarningText(container: Element) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    let node = walker.nextNode()

    while (node) {
      if (node.parentElement?.hasAttribute('data-le-arena-restricted-view-label')) {
        node = walker.nextNode()
        continue
      }

      if (node.nodeValue?.trim()) node.nodeValue = ''
      node = walker.nextNode()
    }
  }

  function getWarningContainer(icon: Element) {
    let element = icon.parentElement

    while (element && element !== root) {
      if (element.classList.contains('igo:bg-white') && element.classList.contains('igo:content-center')) return element
      element = element.parentElement
    }
  }

  root.querySelectorAll('acl-icon').forEach((icon) => {
    const warningContainer = getWarningContainer(icon)
    if (!warningContainer) return
    const warningContent = icon.parentElement ?? warningContainer

    const labelElement = warningContent.querySelector('[data-le-arena-restricted-view-label]') ?? document.createElement('span')
    labelElement.setAttribute('data-le-arena-restricted-view-label', 'true')
    if (labelElement.textContent !== restrictedViewLabel) labelElement.textContent = restrictedViewLabel

    clearWarningText(warningContainer)

    if (!labelElement.parentElement) warningContent.appendChild(labelElement)
  })

  if (seatMessages.size === 0) return

  root.querySelectorAll('.igo\\:flex-col').forEach((tooltipCard) => {
    if (!tooltipCard.classList.contains('igo:bg-white')) return

    const matchingMessage = Array.from(seatMessages.entries()).find(([label]) => tooltipCard.textContent?.includes(label))?.[1]
    const existingLabel = tooltipCard.querySelector('[data-le-arena-seat-message-label]')
    if (!matchingMessage) {
      existingLabel?.remove()
      return
    }

    const labelElement = existingLabel ?? document.createElement('span')
    labelElement.setAttribute('data-le-arena-seat-message-label', 'true')
    labelElement.classList.add('igo:block', 'igo:mt-2', 'igo:text-center')
    labelElement.textContent = matchingMessage
    if (!labelElement.parentElement) tooltipCard.appendChild(labelElement)
  })
}

function ArenaSeatSelection(props: Props) {
  const { apiBase, displayCurrencyCode, eventId, experienceId, onSelectionComplete, options = defaultArenaOptions, perfId, scriptSrc = ARENA_SCRIPT_SRC, selectedTimeSlot, tyrBase } = props
  const intl = useIntl()
  const arenaElementRef = useRef<HTMLElement | null>(null)
  const arenaAppRef = useRef<ArenaApp | undefined>(undefined)
  const arenaSeatMessagesRef = useRef<Map<string, string>>(new Map())
  const displayCurrencyCodeRef = useRef(displayCurrencyCode)
  const selectedTimeSlotRef = useRef(selectedTimeSlot)
  const selectionSessionTokenRef = useRef<string | undefined>(undefined)
  const sendMethodRef = useRef<ArenaSendMethod | undefined>(undefined)
  const trolleyTokenRef = useRef<string | undefined>(undefined)
  const pendingTrolleyTokenResolverRef = useRef<((token: string) => void) | undefined>(undefined)
  const [currency, setCurrency] = useState<ArenaCurrency | undefined>()
  const [legend, setLegend] = useState<Array<ArenaSeatAvailabilityLegend>>([])
  const [selectedSeats, setSelectedSeats] = useState<Array<ArenaSelectedSeat>>([])
  const [, setTrolleyToken] = useState<string | undefined>()
  const [status, setStatus] = useState<ArenaStatus>('idle')
  const [error, setError] = useState<ArenaErrorState | undefined>()
  const [arenaResetKey, setArenaResetKey] = useState(0)
  const { mutateAsync: createSelectionSession } = useCreateExperienceSelectionSession()
  displayCurrencyCodeRef.current = displayCurrencyCode
  selectedTimeSlotRef.current = selectedTimeSlot

  const handleArenaEvent = useCallback((eventName: ArenaEventName, payload: ArenaEventPayload) => {
    const update = getArenaEventStateUpdate(eventName, payload)
    if (update.status) setStatus(update.status)
    if ('error' in update) {
      setError(update.error ? { type: update.error, eventName: update.failedEventName } : undefined)
    }
    if ('currency' in update) setCurrency(update.currency)
    if (update.legend) {
      const customLegend = getArenaCustomLegend(update.legend, selectedTimeSlotRef.current, displayCurrencyCodeRef.current)
      arenaAppRef.current?.setCustomLegend?.(customLegend)
      setLegend(update.legend)
    }
    if (update.seats) {
      arenaSeatMessagesRef.current = getArenaCustomerSeatHoverMessages(update.seats)
    }
    if (update.selectedSeats) setSelectedSeats(update.selectedSeats)
    if ('sendMethod' in update) sendMethodRef.current = update.sendMethod
    if (update.shouldClearTrolleyToken) {
      trolleyTokenRef.current = undefined
      setTrolleyToken(undefined)
    }
    if ('trolleyToken' in update) {
      trolleyTokenRef.current = update.trolleyToken
      setTrolleyToken(update.trolleyToken)
      if (update.trolleyToken) {
        pendingTrolleyTokenResolverRef.current?.(update.trolleyToken)
        pendingTrolleyTokenResolverRef.current = undefined
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let arenaApp: ArenaApp | undefined
    const listeners = new Map<ArenaEventName, (payload: ArenaEventPayload) => void>()

    async function initialiseArena() {
      setStatus('creating-session')
      setError(undefined)
      setCurrency(undefined)
      setLegend([])
      setSelectedSeats([])
      selectionSessionTokenRef.current = undefined
      sendMethodRef.current = undefined
      trolleyTokenRef.current = undefined
      pendingTrolleyTokenResolverRef.current = undefined
      setTrolleyToken(undefined)
      let selectionSession
      try {
        selectionSession = await createSelectionSession(experienceId)
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'selection-session-failed' })
        }
        return
      }

      if (cancelled) return
      selectionSessionTokenRef.current = selectionSession.sessionToken

      setStatus('loading-script')
      try {
        await loadArenaScript(scriptSrc)
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'script-load-failed' })
        }
        return
      }

      if (cancelled) return

      if (!window.IngressoArena) {
        setStatus('failed')
        setError({ type: 'arena-api-unavailable' })
        return
      }

      setStatus('creating-arena')
      let app
      try {
        app = await window.IngressoArena.create({
          apiAuthToken: selectionSession.sessionToken,
          apiBase,
          eventId,
          perfId,
          tyrBase,
          options,
        })
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'arena-create-failed' })
        }
        return
      }

      if (cancelled) {
        app.destroy?.()
        return
      }

      arenaAppRef.current = app
      arenaApp = app
      arenaEvents.forEach((eventName) => {
        const listener = (payload: ArenaEventPayload) => handleArenaEvent(eventName, payload)
        listeners.set(eventName, listener)
        app.on(eventName, listener)
      })
      setStatus('ready')
    }

    initialiseArena().catch(() => {
      if (!cancelled) {
        setStatus('failed')
        setError({ type: 'selection-session-failed' })
      }
    })

    return () => {
      cancelled = true
      listeners.forEach((listener, eventName) => {
        arenaApp?.off?.(eventName, listener)
      })
      listeners.clear()
      arenaApp?.destroy?.()
      arenaAppRef.current = undefined
      selectionSessionTokenRef.current = undefined
      sendMethodRef.current = undefined
      pendingTrolleyTokenResolverRef.current = undefined
    }
  }, [apiBase, arenaResetKey, createSelectionSession, eventId, experienceId, handleArenaEvent, options, perfId, scriptSrc, tyrBase])

  const totalPrice = useMemo(() => getArenaDisplayTotalPrice(selectedSeats, selectedTimeSlot), [selectedSeats, selectedTimeSlot])
  const hasMatchedTickets = useMemo(() => areAllArenaSeatsMatchedToTickets(selectedSeats, selectedTimeSlot), [selectedSeats, selectedTimeSlot])
  const selectedSeatPointsRequests = useMemo(() => selectedSeats.flatMap((seat) => {
    const price = getArenaDisplaySeatPrice(seat, selectedTimeSlot)
    const request = generateLoyaltyPointsCalculatorExperienceOptions(undefined, {
      offerId: experienceId,
      price,
      skip: price === undefined,
    })
    return request ? [{ request, seatId: seat.uuid ?? seat.seatId }] : []
  }), [experienceId, selectedSeats, selectedTimeSlot])
  const pointsEarnCalculationRequests = useMemo(
    () => selectedSeatPointsRequests.map(({ request }) => request),
    [selectedSeatPointsRequests],
  )
  const { pointsCalculations, totalPointsCalculation } = useLoyaltyPointsEarnCalculator(
    pointsEarnCalculationRequests,
    { useBatch: true },
  )
  const totalEarnablePoints = totalPointsCalculation.data?.points ?? 0
  const pointsBySeatId = useMemo(() => new Map(selectedSeatPointsRequests.map(({ seatId }, index) => [
    seatId,
    pointsCalculations[index]?.data?.points ?? 0,
  ])), [pointsCalculations, selectedSeatPointsRequests])
  const customLegend = useMemo(
    () => getArenaCustomLegend(legend, selectedTimeSlot, displayCurrencyCode).legendChanges,
    [displayCurrencyCode, legend, selectedTimeSlot],
  )
  const availabilityHeading = intl.formatMessage({
    id: 'AUYW4E_GlFaDvZY7slkT',
    defaultMessage: 'Availability',
    description: 'Section heading displayed above the Ingresso Arena seat availability card',
  })
  const seatPriceLegendLabel = intl.formatMessage({
    id: 'H25E5Y5XZ9wG8MDW8cz9',
    defaultMessage: 'Seat price legend',
    description: 'Accessible label for the custom Arena legend that explains seat category prices',
  })
  const heading = intl.formatMessage({
    id: 'TlWxNkjpLr8x3z-5BdbE',
    defaultMessage: 'Select your seats',
    description: 'Heading for the Ingresso Arena seat selection component on the experience offer page',
  })
  const selectedSeatsHeading = intl.formatMessage({
    id: '0KUh2PHni3PgNeT5SN_U',
    defaultMessage: 'Selected seats',
    description: 'Heading for the selected seats summary below the Ingresso Arena map',
  })
  const restrictedViewFallback = intl.formatMessage({
    id: 'KRBnKMohpp4B0QQY5Mi2',
    defaultMessage: 'Warning: this seat has a restricted view',
    description: 'Fallback label shown when an Arena selected seat is marked as restricted view without detail text',
  })
  const restrictedViewHoverLabel = intl.formatMessage({
    id: 'TIiNcd6AcVWfarEOs2gm',
    defaultMessage: 'Restricted view',
    description: 'Short warning label shown in the Ingresso Arena seat hover when a seat has a restricted view',
  })
  const totalLabel = intl.formatMessage({
    id: 'AqxTyVgxaMgFk9YTgJfC',
    defaultMessage: 'total',
    description: 'Short label displayed after the formatted price in the Ingresso Arena booking footer',
  })
  const loyaltyPointsTooltip = intl.formatMessage({
    id: 'qt2bdxh29TEr3_NYydFC',
    defaultMessage: 'Loyalty points are earned after your booking is completed.',
    description: 'Tooltip explaining when customers receive loyalty points for an Arena seat reservation',
  })
  const addToCartLabel = intl.formatMessage({
    id: 'S02_Q7wrY0r14pbuTWqx',
    defaultMessage: 'Add to cart',
    description: 'Accessible label for the Arena booking footer add-to-cart icon button',
  })
  const removeSelectedSeatLabel = intl.formatMessage({
    id: 'BfzMVc4j4S4TCkljQ45q',
    defaultMessage: 'Remove selected seat',
    description: 'Accessible label for the non-interactive selected Arena seat removal control shown in the seat summary',
  })
  const continueLabel = intl.formatMessage({
    id: 'eCd5w1sk8iA15UbrgEt2',
    defaultMessage: 'Book now',
    description: 'Button label to reserve selected seats in the Ingresso Arena component',
  })
  const handleReservationError = useCallback((nextError: ArenaErrorState) => {
    setStatus('failed')
    setError(nextError)
    showSnackbar(getErrorLabel(intl, nextError), 'critical')
    setArenaResetKey(key => key + 1)
  }, [intl])

  useEffect(() => {
    let observer: MutationObserver | undefined
    let attempts = 0
    let animationFrame: number | undefined
    let timeout: number | undefined
    let zoomPatchTimeout: number | undefined
    let shadowRoot: ShadowRoot | undefined
    let handleZoomInteraction: (() => void) | undefined

    function patchArenaDom() {
      if (!shadowRoot) return
      if (animationFrame) return

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = undefined
        patchArenaSeatHoverMessages(shadowRoot!, restrictedViewHoverLabel, arenaSeatMessagesRef.current)
        patchArenaZoomControls(shadowRoot!)
      })
    }

    function scheduleArenaZoomControlPatch(delay = 0) {
      if (zoomPatchTimeout) window.clearTimeout(zoomPatchTimeout)
      zoomPatchTimeout = window.setTimeout(() => {
        zoomPatchTimeout = undefined
        if (shadowRoot) patchArenaZoomControls(shadowRoot)
      }, delay)
    }

    function observeArenaShadowRoot() {
      const nextShadowRoot = arenaElementRef.current?.shadowRoot
      if (!nextShadowRoot) {
        attempts += 1
        if (attempts < 20) timeout = window.setTimeout(observeArenaShadowRoot, 250)
        return
      }

      shadowRoot = nextShadowRoot
      patchArenaDom()

      handleZoomInteraction = () => {
        patchArenaDom()
        scheduleArenaZoomControlPatch(550)
      }
      shadowRoot.addEventListener('wheel', handleZoomInteraction, { passive: true })
      shadowRoot.addEventListener('click', handleZoomInteraction)

      observer = new MutationObserver(patchArenaDom)
      observer.observe(shadowRoot, { childList: true, characterData: true, subtree: true })
    }

    observeArenaShadowRoot()

    return () => {
      observer?.disconnect()
      if (shadowRoot && handleZoomInteraction) {
        shadowRoot.removeEventListener('wheel', handleZoomInteraction)
        shadowRoot.removeEventListener('click', handleZoomInteraction)
      }
      if (animationFrame) window.cancelAnimationFrame(animationFrame)
      if (timeout) window.clearTimeout(timeout)
      if (zoomPatchTimeout) window.clearTimeout(zoomPatchTimeout)
    }
  }, [restrictedViewHoverLabel, status])

  useEffect(() => {
    const arenaApp = arenaAppRef.current
    if (!arenaApp || status !== 'ready') return

    const handleArenaZoomChange = () => {
      const shadowRoot = arenaElementRef.current?.shadowRoot
      if (!shadowRoot) return

      window.setTimeout(() => {
        patchArenaZoomControls(shadowRoot)
      }, 550)
    }

    const zoomEvents = ['zoom-in', 'zoom-out', 'reset-chart'] as const
    zoomEvents.forEach((eventName) => {
      arenaApp.on(eventName, handleArenaZoomChange)
    })

    return () => {
      zoomEvents.forEach((eventName) => {
        arenaApp.off?.(eventName, handleArenaZoomChange)
      })
    }
  }, [arenaResetKey, status])

  const handleContinue = useCallback(async() => {
    if (!arenaAppRef.current || selectedSeats.length === 0) return

    setStatus('reserving')
    setError(undefined)
    const reservedSeats = selectedSeats

    try {
      let nextTrolleyToken: string | undefined
      let reservationHold: ArenaReservationHold | undefined

      if (hasMixedArenaReservableSeatGroups(reservedSeats)) {
        if (!selectionSessionTokenRef.current || !sendMethodRef.current) {
          handleReservationError({ type: 'reserve-failed' })
          return
        }

        nextTrolleyToken = await createArenaTrolleyToken({
          apiAuthToken: selectionSessionTokenRef.current,
          apiBase,
          perfId,
          selectedSeats: reservedSeats,
          sendMethod: sendMethodRef.current,
        })
      } else {
        const reserveResult = await arenaAppRef.current.reserveSeats?.()
        if (reserveResult && typeof reserveResult !== 'string') {
          reservationHold = getArenaReservationHold(reserveResult)
          if (!reservationHold) {
            const transactionUuid = reserveResult.trolley_contents?.transaction_uuid
            if (transactionUuid && selectionSessionTokenRef.current) {
              await releaseArenaTransaction({
                apiAuthToken: selectionSessionTokenRef.current,
                apiBase,
                transactionUuid,
              })
            }

            handleReservationError({
              type: 'reserve-failed',
              reservationFailureReason: getArenaReservationFailureReason(reserveResult),
            })
            return
          }

          nextTrolleyToken = getTrolleyToken(reserveResult)
        } else {
          nextTrolleyToken = getTrolleyToken(reserveResult) ?? trolleyTokenRef.current ?? await waitForTrolleyToken(pendingTrolleyTokenResolverRef)
        }
      }

      if (!nextTrolleyToken) {
        handleReservationError({ type: 'missing-trolley-token' })
        return
      }

      if (!reservationHold) {
        if (!selectionSessionTokenRef.current || !sendMethodRef.current) {
          handleReservationError({ type: 'reserve-failed' })
          return
        }

        const reservation = await createArenaTrolleyReservation({
          apiAuthToken: selectionSessionTokenRef.current,
          apiBase,
          sendMethod: sendMethodRef.current,
          trolleyToken: nextTrolleyToken,
        })
        if (!reservation.isValid) {
          handleReservationError({ type: 'reserve-failed', reservationFailureReason: reservation.failureReason })
          return
        }

        reservationHold = {
          reservationExpiresAt: reservation.reservationExpiresAt,
          reservationToken: reservation.reservationToken,
        }
      }

      trolleyTokenRef.current = nextTrolleyToken
      setTrolleyToken(nextTrolleyToken)
      setStatus('reserved')
      onSelectionComplete({
        currency,
        reservationExpiresAt: reservationHold.reservationExpiresAt,
        reservationToken: reservationHold.reservationToken,
        selectedSeats: reservedSeats,
        trolleyToken: nextTrolleyToken,
        totalPrice,
      })
    } catch {
      handleReservationError({ type: 'reserve-failed' })
    }
  }, [apiBase, currency, handleReservationError, onSelectionComplete, perfId, selectedSeats, totalPrice])

  const handleSeatRemove = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const shadowRoot = arenaElementRef.current?.shadowRoot
    if (!shadowRoot) return

    removeArenaSeat(shadowRoot, event.currentTarget.dataset.seatUuid)
  }, [])

  return (
    <Container data-testid="arena-seat-selection">
      <AvailabilityHeading variant="heading4">{availabilityHeading}</AvailabilityHeading>
      <Shell>
        <Header>
          <Heading variant="heading4">{heading}</Heading>
          {error && !isReservationError(error) && <ErrorPill>
            <LineExclamationCircleIcon colour="warning" size="S" />
            <BodyText variant="small" weight="bold">
              {getErrorLabel(intl, error)}
            </BodyText>
          </ErrorPill>}
        </Header>
        <Content>
          <ArenaPanel data-testid="arena-map-panel">
            {customLegend.length > 0 && <ArenaLegend aria-label={seatPriceLegendLabel}>
              {customLegend.map((item, index) => item.price !== undefined && <ArenaLegendItem key={`${item.ticketTypeCode}-${item.priceBandCode}-${item.discountCode}`}>
                <ColourSwatch $bgColor={defaultArenaOptions.legendColours?.[index]?.bgColor} $borderColor={defaultArenaOptions.legendColours?.[index]?.borderColor} />
                <BodyText variant="small"><FormatCurrency value={item.price} currency={displayCurrencyCode} /></BodyText>
              </ArenaLegendItem>)}
            </ArenaLegend>}
            {React.createElement('igo-arena', { key: arenaResetKey, ref: arenaElementRef })}
          </ArenaPanel>
          {selectedSeats.length > 0 && (
            <SelectedSeatsSection aria-labelledby="arena-selected-seats-heading" data-testid="arena-selected-seats">
              <Heading id="arena-selected-seats-heading" variant="heading4">{selectedSeatsHeading}</Heading>
              <SeatList>
                {selectedSeats.map((seat) => {
                  const seatPrice = getArenaDisplaySeatPrice(seat, selectedTimeSlot)
                  const seatPoints = pointsBySeatId.get(seat.uuid ?? seat.seatId) ?? 0
                  const seatLabel = [seat.label ?? seat.seatId, seat.ticketTypeDesc ?? seat.seatBlock?.desc].filter(Boolean).join(' · ')
                  return (
                    <SeatItem key={seat.uuid ?? seat.seatId}>
                      <ColourSwatch $bgColor={seat.bgColor} $borderColor={seat.borderColor} />
                      <SeatDetails>
                        <BodyText variant="large" weight="bold">{seatLabel}</BodyText>
                        <SeatPoints>
                          <PriceRowLoyaltyPoints
                            earnablePoints={seatPoints}
                            calculationType="exact"
                            size="S"
                            tier="bronze"
                          />
                          <Tooltip description={loyaltyPointsTooltip}>
                            <LineInfoCircleIcon colour="neutral-two" size="S" />
                          </Tooltip>
                        </SeatPoints>
                      </SeatDetails>
                      {seatPrice !== undefined && (
                        <SeatPrice variant="large" weight="bold">
                          <FormatCurrency value={seatPrice} currency={displayCurrencyCode} />
                        </SeatPrice>
                      )}
                      <SeatRemoveButton aria-label={removeSelectedSeatLabel} data-seat-uuid={seat.uuid} kind="secondary" onClick={handleSeatRemove} shape="circle" size="medium" variant="dark">
                        <LineTrashAltIcon />
                      </SeatRemoveButton>
                      {seat.isRestrictedView && (
                        <SeatRestrictionWarning variant="small" colour="critical">
                          {restrictedViewFallback}
                        </SeatRestrictionWarning>
                      )}
                    </SeatItem>
                  )
                })}
              </SeatList>
            </SelectedSeatsSection>
          )}
          <BookingFooter>
            <BookingFooterCopy>
              <PriceRowPrice price={totalPrice} saleUnit={totalLabel} size="L" />
              <PriceRowTaxesAndFees />
            </BookingFooterCopy>
            <EarnPoints>
              <PriceRowLoyaltyPoints
                earnablePoints={totalEarnablePoints}
                calculationType="exact"
                size="S"
                tier="bronze"
              />
              <Tooltip description={loyaltyPointsTooltip}>
                <LineInfoCircleIcon colour="neutral-two" size="S" />
              </Tooltip>
            </EarnPoints>
            <CartButton
              aria-label={addToCartLabel}
              kind="secondary"
              nonInteractable
              shape="circle"
              size="large"
              variant="dark"
            >
              <LineShoppingCartIcon />
            </CartButton>
            <BookNowButton kind="primary" shape="round" size="large" disabled={selectedSeats.length === 0 || !hasMatchedTickets || status === 'reserving'} onClick={handleContinue}>
              {continueLabel}
            </BookNowButton>
          </BookingFooter>
        </Content>
      </Shell>
    </Container>
  )
}

export default React.memo(ArenaSeatSelection)
